import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, ExpandableSection, Header, SpaceBetween, Spinner, StatusIndicator } from '@amzn/awsui-components-react-v3';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { UrlHelper } from '../../utils/url-helper';
import { QUERY_KEYS, CACHE_TIMES } from './constants';
import { WebsiteViewService } from 'src/model/WebsiteViewService';
import { useWebsiteSummaryStyles } from './styles/website-summary.styles';

export const WebsiteSummary = React.memo((): JSX.Element | null => {
    useLocation();
    const website = UrlHelper.getParamValue('website');
    const classes = useWebsiteSummaryStyles();

    const { data: summaryData, isFetching: summaryLoading, error: summaryError } = useQuery({
        queryKey: [QUERY_KEYS.WEBSITE_SUMMARY, website],
        queryFn: () => new WebsiteViewService().getWebsiteSummary(website || ''),
        enabled: !!website,
        staleTime: CACHE_TIMES.STALE_TIME,
        cacheTime: CACHE_TIMES.CACHE_TIME,
        retry: false,
    });

    return (
      <Box margin={{ bottom: "l" }}>
        {/* Collapsed on arrival, and only ever opened by the reader. The summary is generated prose of
            unbounded length, so expanded by default it pushes the timeline and every panel below it off the
            first screen for a reader who came for the numbers. `defaultExpanded` is absent rather than set
            to false, which is the same initial state but leaves the section uncontrolled — so nothing here
            can reopen or re-close it behind the reader as the summary resolves.

            The request still fires on mount, not on first expand: the summary is ready the moment the
            section is opened, at the cost of generating one for readers who never open it. */}
        <ExpandableSection
          variant="container"
          data-testid="website-summary-section"
          header={<Header variant="h2"> Website Summary</Header>}
        >
            {summaryLoading ? (
              <SpaceBetween direction="horizontal" size="xs">
                <Spinner />
                <span>Generating summary...</span>
              </SpaceBetween>
            ) : summaryError ? (
              <StatusIndicator type="error">Failed to generate summary.</StatusIndicator>
            ) : summaryData?.summary ? (
              <div className={classes.markdown}>
                <ReactMarkdown>{summaryData.summary}</ReactMarkdown>
              </div>
            ) : (
              <Box variant="p">Summary not available.</Box>
            )}
        </ExpandableSection>
      </Box>
    );
});

WebsiteSummary.displayName = 'WebsiteSummary';
